import { useNavigate } from 'react-router-dom';
import { Users, ChartCandlestick, Handshake, ShoppingCart, PackageOpen } from "lucide-react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { ModuleGrid } from "../layouts/ModuleGrid";
import { OverviewKPIs } from "../features/overview/components/overview-kpis";
import { OrdersTable } from "../features/overview/components/orders-table";
import { RevenueChart } from "../features/overview/components/revenue-chart";
import { getUserInfo } from "../utils/getUserInfo";
import { useMemo, useState } from 'react';
import { format, startOfMonth, endOfMonth } from "date-fns";
import type { DateRange } from "react-day-picker";
import { DateRangePicker } from '../features/ui/date-range-picker';
import { useIsPortraitMobile } from '../hooks/useIsPortraiteMobile';
import { FilterSummaryBar } from '../features/overview/components/FilterSummaryBar';
import { FilteredKpiSummary } from '../features/overview/components/FilteredKpiSummary';
import { useFilteredPerformance, type FilterSelections } from '../features/overview/hooks/useFilteredPerformance';
import { useOverviewKpis } from '../features/overview/hooks/useOverviewKpis';
import TiltMessage from '../components/feedback/TiltMessage';
import { ChannelPerformanceChart } from '../features/managerial/components/channel-performance-chart';
import { ExpenseReport } from '../features/accounting/components/expense-report';
import { RevenueOverview } from '../features/accounting/components/revenue-overview';
import { SalesByTime } from '../features/sales/components/sales-by-time';
import { ProductLifecycleTable } from '../features/managerial/components/product-lifecycle-table';
import { CumulativeCategoryPerformanceChart } from '../features/overview/components/cumulativeCategoryPerformance';
import { Card } from '../features/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../features/ui/tabs';
import { SummaryHeader } from '../features/ui/SummaryHeader';
import { CumulativeStylePerformanceChart } from '../features/overview/components/cumulativeStylePerformance';
import { CumulativeGenderPerformanceChart } from '../features/overview/components/cumulativeGenderPerformance';
import { CumulativeCollectionPerformanceChart } from '../features/overview/components/cumulativeCollectionPerformance';
import { CumulativeColorPerformanceChart } from '../features/overview/components/cumulativeColorPerformance';
import { MonthYearPicker } from '../features/ui/month-Year-Picker';

// Updated role access configuration to match the sidebar navigation
const ROLE_ACCESS = {
  // Everyone can see overview
  ecommerce: ['admin', 'executive', 'ecommerce'],
  retail: ['admin', 'executive', 'retail'],
  wholesale: ['admin', 'executive', 'wholesale'],
  ecommerce_sales: ['ecommerce'],
  ecommerce_marketing: ['ecommerce'],
  ecommerce_customer: ['ecommerce'],
  retail_sales: ['retail'],
  retail_marketing: ['retail'],
  retail_customer: ['retail'],
  wholesale_sales: ['wholesale'],
  wholesale_marketing: ['wholesale'],
  wholesale_customer: ['wholesale'],
};

export default function DashboardOverview() {
  const now = new Date();
  const [date, setDate] = useState<DateRange | undefined>({
    from: new Date(now.getFullYear(), now.getMonth(), 1),
    to: now,
  })
  const [categorySelectedDate, setCategorySelectedDate] = useState<Date | undefined>(new Date(now.getFullYear(), now.getMonth()));
  const showTiltMessage = useIsPortraitMobile();

  const startDate = useMemo(() => date?.from ? format(date.from, "yyyy-MM-dd") : "", [date]);
  const endDate = useMemo(() => date?.to ? format(date.to, "yyyy-MM-dd") : "", [date]);
  const navigate = useNavigate();
  const user = getUserInfo();
  const userGroups = user?.groups || [];

  // Filter state for quick filter summary
  const [filters, setFilters] = useState<FilterSelections>({
    channels: [],
    categories: [],
    genders: [],
    collections: [],
    colors: [],
    styles: [],
  });

  // Fetch overview data including cumulative performance
  const { data: overviewData, isLoading: isOverviewLoading } = useOverviewKpis({ 
    start_date: startDate, 
    end_date: endDate 
  });

  // Extract cumulative performance from overview data
  const cumulativePerformance = overviewData?.[1]?.cumulative_performance;

  // Calculate filtered metrics
  const filteredMetrics = useFilteredPerformance(cumulativePerformance, filters);

  // Helper function to check if user has access to a section
  const canAccess = (section: keyof typeof ROLE_ACCESS): boolean => {
    if (!userGroups || userGroups.length === 0) return false;

    // Check if any of the user's groups match the allowed roles (case-insensitive)
    return userGroups.some(group =>
      ROLE_ACCESS[section].includes(group.toLowerCase())
    );
  };

  // Navigation items configuration
  const navItems = [
    {
      label: 'Ecommerce',
      icon: <ChartCandlestick className="h-4 w-4 sm:h-6 sm:w-6 mb-[.15em]" strokeWidth={2.5} />,
      path: '/ecommerce',
      key: 'ecommerce' as keyof typeof ROLE_ACCESS
    },
    {
      label: 'Marketing',
      icon: <Handshake className="h-4 w-4 sm:h-6 sm:w-6 mb-[.15em]" strokeWidth={2.5} />,
      path: '/ecomm-marketing',
      key: 'ecommerce_marketing' as keyof typeof ROLE_ACCESS
    },
    {
      label: 'Sales',
      icon: <ShoppingCart className="h-4 w-4 sm:h-6 sm:w-6 mb-[.15em]" strokeWidth={2.5} />,
      path: '/ecomm-sales',
      key: 'ecommerce_sales' as keyof typeof ROLE_ACCESS
    },
    {
      label: 'Customer',
      icon: <Users className="h-4 w-4 sm:h-6 sm:w-6 mb-[.15em]" strokeWidth={2.5} />,
      path: '/ecomm-customer',
      key: 'ecommerce_customer' as keyof typeof ROLE_ACCESS
    },
    {
      label: 'Wholesale',
      icon: <PackageOpen className="h-4 w-4 sm:h-6 sm:w-6 mb-[.15em]" strokeWidth={2.5} />,
      path: '/wholesale',
      key: 'wholesale' as keyof typeof ROLE_ACCESS
    },
    {
      label: 'Marketing',
      icon: <PackageOpen className="h-4 w-4 sm:h-6 sm:w-6 mb-[.15em]" strokeWidth={2.5} />,
      path: '/wholesale-marketing',
      key: 'wholesale_marketing' as keyof typeof ROLE_ACCESS
    },
    {
      label: 'Sales',
      icon: <PackageOpen className="h-4 w-4 sm:h-6 sm:w-6 mb-[.15em]" strokeWidth={2.5} />,
      path: '/wholesale-sales',
      key: 'wholesale_sales' as keyof typeof ROLE_ACCESS
    },
    {
      label: 'Customer',
      icon: <PackageOpen className="h-4 w-4 sm:h-6 sm:w-6 mb-[.15em]" strokeWidth={2.5} />,
      path: '/wholesale-customer',
      key: 'wholesale_customer' as keyof typeof ROLE_ACCESS
    },
    {
      label: 'Retail',
      icon: <PackageOpen className="h-4 w-4 sm:h-6 sm:w-6 mb-[.15em]" strokeWidth={2.5} />,
      path: '/retail',
      key: 'retail' as keyof typeof ROLE_ACCESS
    },
    {
      label: 'Marketing',
      icon: <PackageOpen className="h-4 w-4 sm:h-6 sm:w-6 mb-[.15em]" strokeWidth={2.5} />,
      path: '/retail-marketing',
      key: 'retail_marketing' as keyof typeof ROLE_ACCESS
    },
    {
      label: 'Sales',
      icon: <PackageOpen className="h-4 w-4 sm:h-6 sm:w-6 mb-[.15em]" strokeWidth={2.5} />,
      path: '/retail-sales',
      key: 'retail_sales' as keyof typeof ROLE_ACCESS
    },
    {
      label: 'Customer',
      icon: <PackageOpen className="h-4 w-4 sm:h-6 sm:w-6 mb-[.15em]" strokeWidth={2.5} />,
      path: '/retail-customer',
      key: 'retail_customer' as keyof typeof ROLE_ACCESS
    },
  ];

  // Filter navigation items based on user permissions
  const allowedNavItems = navItems.filter(item => canAccess(item.key));

  // Compute start_date and end_date for the selected category month
  const categoryStartDate = categorySelectedDate
    ? format(startOfMonth(categorySelectedDate), "yyyy-MM-dd")
    : "";
  const categoryEndDate = categorySelectedDate
    ? format(endOfMonth(categorySelectedDate), "yyyy-MM-dd")
    : "";

  return (
    <>
      <TiltMessage show={showTiltMessage} />
      <DashboardLayout>
        <div className={showTiltMessage ? "blur-sm pointer-events-none" : ""}>
          {/* Hero Section - Made responsive */}
          <div className="flex flex-col items-center text-center pt-6 sm:pt-10 pb-8 sm:pb-16 px-4 [text-shadow:0_2px_30px_rgba(0,0,0,.3)]">
            <h1 className="text-xs sm:text-lg font-light text-white uppercase tracking-[.15em] sm:tracking-[.25em]">
              Highest of the Ordinary
            </h1>
            <h2 className="text-xl sm:text-4xl uppercase font-bold tracking-[.05em] sm:tracking-[.1em] text-white mt-3 sm:mt-6 leading-tight">
              Welcome Back, {user?.username || "User"}
            </h2>

            {/* Navigation buttons - Only show allowed items */}
            <div className="flex flex-col md:flex-row gap-2 md:gap-2 mt-6 md:mt-8 items-center justify-center w-full">
              {allowedNavItems.map(({ label, icon, path }) => (
                <div
                  key={label}
                  onClick={() => navigate(path)}
                  className="flex gap-2 items-center justify-center text-xs md:text-sm font-bold uppercase px-4 md:px-6 h-8 md:h-10 rounded-full 
                    backdrop-blur-sm shadow-xl-softer border-2 border-white/60 dark:border-white/20 
                    w-full md:w-auto max-w-xs md:max-w-none
                    text-black dark:text-black bg-white cursor-pointer hover:bg-white/90 transition-all duration-200"
                >
                  {icon}
                  <span className="hidden sm:inline md:inline">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Date picker - Responsive positioning */}
          <div className="w-full px-4 sm:w-fit sm:max-w-xs sm:ml-6 mb-4 sm:mb-0">
            <DateRangePicker dateRange={date} onDateRangeChange={setDate} />
          </div>

          {/* Quick Filter Summary Section */}
          <div className="px-4 sm:px-6 mt-6">
            <FilterSummaryBar
              filters={filters}
              onFiltersChange={setFilters}
              cumulativePerformance={cumulativePerformance}
              isLoading={isOverviewLoading}
            />
            <FilteredKpiSummary
              filteredMetrics={filteredMetrics}
              isLoading={isOverviewLoading}
            />
          </div>

          {/* Divider */}
          <div className="px-4 sm:px-6 my-6">
            <div className="border-t-2 border-primary/20"></div>
          </div>

          {/* Main content grid - Responsive layout */}
          <div className="px-4 sm:px-0">
            <ModuleGrid>
              {/* KPIs - Full width on mobile, spans 6 on desktop */}
              <div className="col-span-12 sm:col-span-6 2xl:col-span-6 flex flex-col h-full">
                <OverviewKPIs start_date={startDate} end_date={endDate} />
              </div>
              {/* Revenue Overview - Full width on mobile, half width on tablet/desktop */}
              <div className="col-span-12 sm:col-span-6 2xl:col-span-3 flex flex-col h-full">
                <RevenueOverview />
              </div>

              {/* Expense Report - Full width on mobile, half width on tablet/desktop */}
              <div className="col-span-12 sm:col-span-6 2xl:col-span-3 flex flex-col h-full">
                <ExpenseReport />
              </div>
              {/* Channel Performance - Full width on mobile, half width on tablet/desktop */}
              <div className="col-span-12 sm:col-span-6 row-span-1 sm:row-span-2 2xl:row-span-1 flex flex-col h-64 sm:h-full mb-4 sm:mb-0">
                <ChannelPerformanceChart />
              </div>
              <div className="col-span-6 flex flex-col h-full">
                <SummaryHeader
                            title="Cumulative Category Performance"
                        />
                        <Card>
                          <Tabs defaultValue="general">
                                <div className="flex justify-between items-center w-full">
                                <TabsList className="m-4">
                                    <TabsTrigger value="general">General</TabsTrigger>
                                    <TabsTrigger value="gender">Gender</TabsTrigger>
                                    <TabsTrigger value="collection">Collection</TabsTrigger>
                                    <TabsTrigger value="color">Color</TabsTrigger>
                                    <TabsTrigger value="style">Style</TabsTrigger>
                                    
                                </TabsList>
                                <div className="pr-3"><MonthYearPicker selected={categorySelectedDate} onChange={setCategorySelectedDate} /></div>
                                </div>
                                <TabsContent value="general">
                                    <div className="col-span-6 flex flex-col h-full">
                                        <CumulativeCategoryPerformanceChart start_date={categoryStartDate} end_date={categoryEndDate} />
                                    </div>
                                </TabsContent>
                                <TabsContent value="gender">
                                    <div className="col-span-6 flex flex-col h-full">
                                        <CumulativeGenderPerformanceChart start_date={categoryStartDate} end_date={categoryEndDate} />
                                    </div>
                                </TabsContent>
                                <TabsContent value="collection">
                                    <div className="col-span-6 flex flex-col h-full">
                                        <CumulativeCollectionPerformanceChart start_date={categoryStartDate} end_date={categoryEndDate} />
                                    </div>
                                </TabsContent>
                                <TabsContent value="color">
                                    <div className="col-span-6 flex flex-col h-full">
                                        <CumulativeColorPerformanceChart start_date={categoryStartDate} end_date={categoryEndDate} />
                                    </div>
                                </TabsContent>
                                <TabsContent value="style">
                                    <div className="col-span-6 flex flex-col h-full">
                                        <CumulativeStylePerformanceChart start_date={categoryStartDate} end_date={categoryEndDate} />
                                    </div>
                                </TabsContent>
                              
                            </Tabs>
                        </Card>
              </div>

              {/* Revenue Chart - Full width, adjust height on mobile */}
              <div className="col-span-12 sm:col-span-6 row-span-1 sm:row-span-2 2xl:row-span-1 flex flex-col h-64 sm:h-full mb-4 sm:mb-0">
                <RevenueChart />
              </div>
              {/* Sales by time - Full width */}
              <div className="col-span-12 sm:col-span-6 flex flex-col h-full">
                <SalesByTime />
              </div>

              {/* Orders Table - Full width */}
              <div className="col-span-12 sm:col-span-6 flex flex-col h-full">
                <OrdersTable />
              </div>

              <div className="col-span-6 flex flex-col h-full">
                <ProductLifecycleTable />
              </div>
            </ModuleGrid>
          </div>
        </div>
      </DashboardLayout>
    </>
  );
}